import { FC } from "react";

import { Box, BoxProps, Stack, useTheme } from "@mui/material";

import { BaseStep1LayoutProps } from "./BaseStep1LayoutProps";

import { FormRegisteredCharityStatement } from "@/canvas/features/FormV2/blocks/shell/FormRegisteredCharityStatement";
import { ThemedZeffyLogo } from "@/canvas/layouts/ThemedBranding/ThemedZeffyLogo";
import { useHasValueChanged } from "@/common/hooks/useHasValueChanged";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";

type BaseStep1TwoColumnLayoutProps = Pick<
  BaseStep1LayoutProps,
  | "slots"
  | "contextDrawerHeight"
  | "showCheckoutFooter"
  | "showMobileChooseTicketsFloatingButton"
  | "logoHref"
  | "onLogoClick"
> & {
  isSquareBanner?: boolean;
};

export const BaseStep1TwoColumnLayout: FC<BaseStep1TwoColumnLayoutProps> = ({
  slots,
  contextDrawerHeight,
  showCheckoutFooter = false,
  logoHref,
  onLogoClick,
  isSquareBanner = false,
}) => {
  const theme = useTheme();
  const { isSmallScreen } = useMediaQuery();

  const hasBanner = Boolean(slots.banner);
  const hasWelcomeBackBanner = Boolean(slots.welcomeBackBanner);
  const [animateCheckoutFooter, watchedShowCheckoutFooter] = useHasValueChanged(showCheckoutFooter);

  const renderColumn = (
    content: React.ReactNode,
    sx: {
      width: BoxProps["width"];
      paddingInlineStart: BoxProps["paddingInlineStart"];
      paddingInlineEnd: BoxProps["paddingInlineEnd"];
    }
  ) => {
    return (
      <Box
        sx={{
          width: sx.width,
          paddingBlockStart: hasWelcomeBackBanner ? { md: 6, lg: 6 } : { md: 8, lg: 10 },
          paddingBlockEnd: { md: 8, lg: 10 },
          paddingInlineStart: sx.paddingInlineStart,
          paddingInlineEnd: sx.paddingInlineEnd,
        }}
      >
        {content}
      </Box>
    );
  };

  const renderAnimatedCheckoutFooter = () => {
    const hidden = !watchedShowCheckoutFooter && !animateCheckoutFooter;

    return (
      <Box
        data-test="form-v2-checkout-footer"
        position={{ xs: "fixed", md: "sticky" }}
        bottom={{ xs: 0, md: theme.spacing(5) }}
        left={0}
        right={0}
        marginBlockStart={{ md: hidden ? 0 : 2 }}
        padding={{ xs: hidden ? 0 : 2, md: 0 }}
        sx={{
          animation: animateCheckoutFooter
            ? watchedShowCheckoutFooter
              ? "fade-slide-in 0.3s ease-in-out both"
              : "fade-slide-out 0.3s ease-in-out both"
            : undefined,
          opacity: hidden ? 0 : undefined,
          height: hidden ? 0 : undefined,
          zIndex: 1000,
          ["@keyframes fade-slide-in"]: {
            from: { translate: "0 20px", opacity: 0 },
            to: { translate: "0 0", opacity: 1 },
          },
          ["@keyframes fade-slide-out"]: {
            from: { translate: "0 0", opacity: 1 },
            to: { translate: "0 20px", opacity: 0 },
          },
        }}
      >
        {slots.checkoutFooter}
      </Box>
    );
  };

  return (
    <Stack>
      {slots.welcomeBackBanner && (
        <Box sx={{ position: { xs: "sticky", md: "static" }, top: 0, zIndex: { xs: 50, md: "auto" } }}>
          {slots.welcomeBackBanner}
        </Box>
      )}
      <Stack gap={{ xs: 0, md: 4 }}>
        <Stack gap={2} width="100%">
          {isSmallScreen && slots.banner}
          <Stack
            direction={{ md: "row" }}
            gap={{
              xs: 1.5,
              md: 7.5,
            }}
            width="100%"
            padding={{ xs: theme.spacing(hasBanner ? 0 : 2, 2, 0, 2), md: 0 }}
          >
            {renderColumn(slots.info, {
              width: {
                md: isSquareBanner ? "calc(43% - 7.5 * 8px)" : "calc(47% - 7.5 * 8px)",
                lg: isSquareBanner ? "calc(43% - 7.5 * 8px)" : "calc(47% - 7.5 * 8px)",
                xl: isSquareBanner ? "calc(43% - 7.5 * 8px)" : "calc(47% - 7.5 * 8px)",
              },
              paddingInlineStart: { md: "15vw", lg: "15vw", xl: "20vw" },

              paddingInlineEnd: { md: 0, lg: 0, xl: 0 },
            })}
            {isSmallScreen && slots.divider}
            {renderColumn(
              <Stack width="100%">
                {slots.order}
                {!isSmallScreen && renderAnimatedCheckoutFooter()}
              </Stack>,
              {
                width: {
                  md: isSquareBanner ? "57%" : "53%",
                  lg: isSquareBanner ? "57%" : "53%",
                  xl: isSquareBanner ? "57%" : "53%",
                },
                paddingInlineStart: { md: 0, lg: 0, xl: 0 },

                paddingInlineEnd: { md: "15vw", lg: "15vw", xl: "20vw" },
              }
            )}
          </Stack>
        </Stack>
        <Box
          sx={{
            paddingInline: { xs: 2, md: "15vw", lg: "15vw", xl: "20vw" },
          }}
        >
          <FormRegisteredCharityStatement />
        </Box>
        <Box
          sx={{
            position: { md: "fixed" },
            top: { md: 24 },
            left: { md: 24 },
            display: { xs: "flex", md: "block" },
            justifyContent: { xs: "center", md: "unset" },
            paddingBlock: { xs: 2, md: 0 },
            zIndex: 1,
          }}
        >
          <ThemedZeffyLogo href={logoHref} onClick={onLogoClick} />
        </Box>
        {slots.shopCartButton && (
          <Box
            sx={{
              position: "fixed",
              top: 24 + contextDrawerHeight,
              right: 24,
              zIndex: 1001,
              transition: "top 0.3s ease-out",
            }}
          >
            {slots.shopCartButton}
          </Box>
        )}
        {isSmallScreen && (
          <>
            {watchedShowCheckoutFooter && <Box sx={{ height: 75, visibility: "hidden" }} />}
            {renderAnimatedCheckoutFooter()}
            {slots.mobileChooseTicketsFloatingButton}
          </>
        )}
      </Stack>
    </Stack>
  );
};
